/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Chip, Tooltip, Typography, makeStyles } from '@material-ui/core';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Progress } from '@backstage/core-components';
import { RepoGraph } from './RepoGraph';
import {
  CatalogEntityLike,
  ConsumptionArc,
  buildConsumptionArcs,
} from './consumptionModel';
import {
  FleetRelations,
  buildCompanyDoc,
  crossRepoLinks,
  parseFleetRelations,
} from './companyGraphModel';
import { KgDirtree } from './types';

// Global's top level (PLAT-1646): the entire company as ONE non-derived
// graph — every box a repo, opened by deep link into that repo's own
// already-served graph. Built from repo-level facts only (the fleet
// dirtree's org bands + the catalog's declared API relations): no giant
// doc, no shards, nothing streamed. Edge families carry their provenance
// in the name, so the legend itself says where each relationship came from.

const useStyles = makeStyles(theme => ({
  intro: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    margin: theme.spacing(0.5, 0, 1),
    maxWidth: 860,
  },
  facts: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
}));

export const CompanyGraph = (props: {
  loadDirtree: (slug: string) => Promise<KgDirtree>;
  onOpenRepo: (slug: string) => void;
}) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [tree, setTree] = useState<KgDirtree | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [arcs, setArcs] = useState<ConsumptionArc[] | null>(null);
  const [arcsError, setArcsError] = useState(false);
  const [unmapped, setUnmapped] = useState(0);
  const [relations, setRelations] = useState<FleetRelations | null>(null);
  const [relationsAbsent, setRelationsAbsent] = useState(false);
  const [relationsError, setRelationsError] = useState(false);

  // Manifest relations, published best-effort by the fleet workflow's
  // relations pass. Only a 404 means "no build has produced them yet" —
  // a 502/network failure is a store error and must never masquerade as
  // absence (the backend built that taxonomy deliberately).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('knowledge-graph');
        const res = await fetchApi.fetch(`${base}/global/artifact/relations`);
        if (cancelled) return;
        if (res.ok) {
          try {
            // Validated at the boundary, never cast: a drifted artifact is
            // a store error, and malformed entries drop item-by-item.
            const parsed = parseFleetRelations(await res.json());
            if (parsed) setRelations(parsed);
            else setRelationsError(true);
          } catch {
            setRelationsError(true);
          }
        } else if (res.status === 404) {
          setRelationsAbsent(true);
        } else {
          setRelationsError(true);
        }
      } catch {
        if (!cancelled) setRelationsError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    props
      .loadDirtree('global')
      .then(t => !cancelled && setTree(t))
      .catch(e => !cancelled && setError(String((e as Error).message ?? e)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!tree) return undefined;
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: 'Component' },
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.annotations',
          'relations',
        ],
      })
      .then(res => {
        if (cancelled) return;
        // Any repo in the fleet's orgs qualifies — INCLUDING ones without a
        // published graph (they render as ghost boxes). Filtering to
        // store-present repos hid 7 of 11 real consumption pairs (audit
        // 2026-08-25); the org prefix still keeps out-of-fleet junk away.
        // Everything here compares LOWERCASE (consumptionModel normalizes
        // its side); buildCompanyDoc canonicalizes endpoints back onto the
        // tree's original spelling — the two-sided case-insensitive rule
        // from the github-org preserves-login-case lesson.
        const orgs = tree.dirs
          .filter(d => d.parent === '' && d.path !== '(root)')
          .map(d => d.path.toLowerCase());
        const entities = res.items as unknown as CatalogEntityLike[];
        const repoSlugs = new Set(
          tree.dirs
            .filter(d => orgs.includes(d.parent.toLowerCase()))
            .map(d => d.path.toLowerCase()),
        );
        for (const e of entities) {
          const slug =
            e.metadata.annotations?.['github.com/project-slug']?.toLowerCase();
          if (slug && orgs.some(o => slug.startsWith(`${o}/`))) {
            repoSlugs.add(slug);
          }
        }
        const built = buildConsumptionArcs(entities, repoSlugs);
        setArcs(built.arcs);
        setUnmapped(built.unmapped);
      })
      .catch(() => !cancelled && setArcsError(true));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree]);

  const found = useMemo(() => (tree ? crossRepoLinks(tree.edges) : []), [tree]);
  const doc = useMemo(
    () => (tree ? buildCompanyDoc(tree, arcs ?? [], found, relations) : null),
    [tree, arcs, found, relations],
  );

  if (error) {
    return (
      <div role="alert">company graph failed to load — {error}</div>
    );
  }
  // The renderer's layout cache is keyed on built_at_commit and assumes an
  // immutable doc — so render only once every edge source has settled
  // (loaded, provably absent, or failed). A tree-only layout cached early
  // would silently swallow every edge that arrived after it.
  const arcsSettled = arcs !== null || arcsError;
  const relationsSettled =
    relations !== null || relationsAbsent || relationsError;
  if (!doc || !arcsSettled || !relationsSettled) return <Progress />;

  const orgOf = (slug: string) => slug.split('/')[0];
  const crossOrgCount =
    found.filter(l => l.crossOrg).length +
    (arcs ?? []).filter(a => a.crossOrg).length +
    new Set(
      (relations?.edges ?? [])
        .filter(e => orgOf(e.from) !== orgOf(e.to))
        .map(e => `${e.from} ${e.to}`),
    ).size;

  return (
    <>
      <Typography className={classes.intro} component="p">
        The whole company in one graph — every box is a repo; open one to load
        its own symbol graph. Edges are cross-repo relationships: API
        consumption declared in the catalog, and links found in the merged
        fleet graph.
      </Typography>
      <div className={classes.facts}>
        <Chip size="small" label={`${doc.nodes.length} repos`} />
        <Chip
          size="small"
          label={`${(doc.links ?? []).length} cross-repo relationship${
            (doc.links ?? []).length === 1 ? '' : 's'
          } · ${crossOrgCount} cross-org`}
        />
        {(doc.links ?? []).length === 0 && (
          <Chip
            size="small"
            variant="outlined"
            label="none found yet — sources: build manifests (next fleet build), catalog consumesApis, merged-graph links (PLAT-1650)"
          />
        )}
        {doc.ghosts.length > 0 && (
          <Tooltip
            title={`${doc.ghosts.join(
              ', ',
            )} — they appear in declared relationships but have no published graph; enrolling them in graph-refresh completes the map`}
          >
            <Chip
              size="small"
              variant="outlined"
              label={`${doc.ghosts.length} repo${
                doc.ghosts.length === 1 ? '' : 's'
              } in relationships without a published graph`}
            />
          </Tooltip>
        )}
        {arcsError && (
          <Tooltip title="the catalog query failed — declared API consumption is missing from this render">
            <Chip size="small" color="secondary" label="catalog unavailable" />
          </Tooltip>
        )}
        {unmapped > 0 && (
          <Tooltip title="declared consumption relations that couldn't be mapped to a fleet-org repo (missing project-slug, provider not in the catalog, or a repo outside the fleet's orgs) — they are absent from the graph">
            <Chip
              size="small"
              variant="outlined"
              label={`${unmapped} consumption relation${unmapped === 1 ? '' : 's'} unmapped`}
            />
          </Tooltip>
        )}
        {relationsAbsent && (
          <Chip
            size="small"
            variant="outlined"
            label="manifest relations not published yet — the next fleet build adds them"
          />
        )}
        {relationsError && (
          <Tooltip title="the relations artifact could not be read — a store error, not an absence; manifest edges are missing from this render">
            <Chip size="small" color="secondary" label="manifest relations unavailable" />
          </Tooltip>
        )}
        {relations?.carried_from && (
          <Tooltip
            title={`this fleet build's relations pass failed; showing the relations carried forward from build ${relations.carried_from}`}
          >
            <Chip
              size="small"
              variant="outlined"
              label="manifest relations from a previous build"
            />
          </Tooltip>
        )}
        {relations && (relations.unconnected ?? []).length > 0 && (
          <Tooltip title={(relations.unconnected ?? []).join(', ')}>
            <Chip
              size="small"
              variant="outlined"
              label={`${(relations.unconnected ?? []).length} repo${
                (relations.unconnected ?? []).length === 1 ? '' : 's'
              } still unconnected`}
            />
          </Tooltip>
        )}
        {relations && Object.keys(relations.unresolved ?? {}).length > 0 && (
          <Tooltip
            title={`internal refs no rule or adjudication could map: ${Object.keys(
              relations.unresolved ?? {},
            )
              .slice(0, 8)
              .join(', ')}${
              Object.keys(relations.unresolved ?? {}).length > 8 ? ', …' : ''
            }`}
          >
            <Chip
              size="small"
              variant="outlined"
              label={`${Object.keys(relations.unresolved ?? {}).length} unresolved refs`}
            />
          </Tooltip>
        )}
        {relations && (relations.skipped ?? []).length > 0 && (
          <Tooltip
            title={`repos whose manifests could not be gathered — their edges are missing from this render: ${(
              relations.skipped ?? []
            )
              .slice(0, 6)
              .join('; ')}`}
          >
            <Chip
              size="small"
              color="secondary"
              label={`${(relations.skipped ?? []).length} repos skipped by the relations pass`}
            />
          </Tooltip>
        )}
      </div>
      <RepoGraph
        key={`company-${doc.built_at_commit ?? ''}`}
        doc={doc}
        repoName="company fleet — one box per repo"
        onOpenNode={props.onOpenRepo}
        openNodeLabel="Open repo graph"
        dirMode
      />
    </>
  );
};
